<template>
  <div class="nav-root">
    <nav class="modern-nav" :class="{ 'collapsed': !showExpanded, 'mobile-open': isMobileOpen }">
      <!-- County logo (same asset path as login page) -->
      <div class="nav-top-logo-wrap" :class="{ 'nav-top-logo-wrap--collapsed': !showExpanded }">
        <img
          class="nav-top-logo"
          :src="navLogoSrc"
          alt="Homa Bay County"
          @error="onNavLogoError"
        >
      </div>

      <!-- Logo and Brand Section -->
      <div class="nav-brand">
        <div class="brand-content" :class="{ 'brand-content--collapsed': !showExpanded }">
          <div class="brand-text" v-if="showExpanded">
            <h3 class="brand-title">Homabay County HR System</h3>
          </div>
          <button
            class="nav-collapse-btn"
            @click="toggleNav"
            type="button"
            :aria-expanded="!isCollapsed"
            :aria-label="isCollapsed ? 'Expand navigation panel' : 'Collapse navigation panel'"
            :title="isCollapsed ? 'Expand navigation panel' : 'Collapse navigation panel'"
          >
            <i class="bx nav-collapse-btn__icon" :class="isCollapsed ? 'bx-chevrons-right' : 'bx-chevrons-left'" aria-hidden="true"></i>
          </button>
        </div>
      </div>

      <div class="nav-profile" v-if="showExpanded" @click.stop>
        <div class="profile-avatar">
          <div class="avatar-circle">
            <i class="bx bx-user"></i>
          </div>
        </div>
        <div class="profile-info">
          <h4 class="profile-name">{{ sessionNames }}</h4>
          <p class="profile-meta" v-if="userTypeLabel">{{ userTypeLabel }}</p>
        </div>
        <div class="profile-actions">
          <button
            type="button"
            class="profile-menu-trigger"
            @click.stop="toggleProfileMenu"
            :aria-expanded="showProfileMenu ? 'true' : 'false'"
            :aria-haspopup="true"
            :aria-label="showProfileMenu ? 'Close account menu' : 'Open account menu'"
            :title="showProfileMenu ? 'Close account menu' : 'Account menu'"
          >
            <i class="bx profile-menu-trigger__icon" :class="showProfileMenu ? 'bx-chevron-up' : 'bx-chevron-down'" aria-hidden="true"></i>
          </button>
        </div>
        <transition name="fade">
          <div class="profile-dropdown" v-if="showProfileMenu" role="menu" aria-label="Account actions" @click.stop>
            <div class="profile-dropdown-header">
              <div class="profile-dropdown-header__avatar" aria-hidden="true">
                <i class="bx bx-user"></i>
              </div>
              <div class="profile-dropdown-header__text">
                <span class="profile-dropdown-header__name">{{ sessionNames }}</span>
                <span class="profile-dropdown-header__role" v-if="userTypeLabel">{{ userTypeLabel }}</span>
              </div>
            </div>
            <div class="dropdown-content">
              <a class="dropdown-item" role="menuitem" href="#" @click.prevent="goTo('employee-profile')">
                <span class="dropdown-item__icon-wrap" aria-hidden="true"><i class="bx bx-user-circle"></i></span>
                <span class="dropdown-item__label">Profile</span>
              </a>
              <a class="dropdown-item" role="menuitem" href="#" @click.prevent="goTo('change-password')">
                <span class="dropdown-item__icon-wrap" aria-hidden="true"><i class="bx bx-lock-open"></i></span>
                <span class="dropdown-item__label">Change Password</span>
              </a>
              <div class="dropdown-divider"></div>
              <a class="dropdown-item dropdown-item--danger" role="menuitem" href="#" @click.prevent="logout('/')">
                <span class="dropdown-item__icon-wrap" aria-hidden="true"><i class="bx bx-log-out"></i></span>
                <span class="dropdown-item__label">Logout</span>
              </a>
            </div>
          </div>
        </transition>
      </div>

      <!-- Navigation Menu -->
      <div class="nav-menu" ref="navMenu">
        <div class="nav-search" v-if="showExpanded">
          <div class="search-wrapper">
            <i class="bx bx-search search-icon"></i>
            <input
              type="text"
              class="search-input"
              placeholder="Search..."
              v-model="searchQuery"
              @input="filterMenuItems"
            >
          </div>
        </div>

        <div class="menu-section" v-for="(section, index) in filteredMenuSections" :key="'nav-sec-' + index + '-' + (section.title || '')">
          <!-- Five HR modules: collapsible dropdown; other sections stay flat like Finance -->
          <button
            v-if="isCollapsibleModuleSection(section.title) && showExpanded"
            type="button"
            class="section-header section-header--dropdown"
            :class="{ 'section-header--dropdown-active': moduleSectionHasActiveRoute(section) }"
            @click.stop="toggleModuleSection(sectionModuleKey(section.title))"
            :aria-expanded="isModuleSectionExpanded(section) ? 'true' : 'false'"
          >
            <span class="section-title">{{ sectionDisplayTitle(section.title) }}</span>
            <i class="bx section-header-chevron" :class="isModuleSectionExpanded(section) ? 'bx-chevron-up' : 'bx-chevron-down'" aria-hidden="true"></i>
          </button>

          <div class="section-header" v-else-if="section.title && showExpanded">
            <h4 class="section-title">{{ sectionDisplayTitle(section.title) }}</h4>
          </div>

          <transition name="slide-down">
            <div
              v-show="!isCollapsibleModuleSection(section.title) || !showExpanded || isModuleSectionExpanded(section)"
              class="menu-items"
            >
            <div v-for="item in section.items" :key="item.id" class="menu-item-wrapper">
              <template v-if="item.children && item.children.length > 0">
                <div
                  class="menu-item-header"
                  :class="{ 'active': isActive(item.route) || isActiveParent(item), 'collapsed': !showExpanded }"
                  @click.stop="handleParentWithChildrenClick(item)"
                >
                  <div class="menu-icon"><i :class="item.icon"></i></div>
                  <div class="menu-text" v-if="showExpanded">
                    <span class="menu-title">{{ item.title }}</span>
                    <span class="menu-badge" v-if="item.badge">{{ item.badge }}</span>
                  </div>
                  <div class="menu-arrow" v-if="showExpanded">
                    <i class="bx" :class="expandedItems[item.id] ? 'bx-chevron-up' : 'bx-chevron-down'"></i>
                  </div>
                  <div class="menu-arrow-collapsed" v-if="!showExpanded && item.children?.length">
                    <i class="bx bx-chevron-right"></i>
                  </div>
                  <div class="menu-tooltip" v-if="!showExpanded">{{ item.title }}</div>
                </div>
                <transition name="slide-down">
                  <div class="submenu" v-show="expandedItems[item.id] && showExpanded">
                    <div
                      v-for="child in item.children"
                      :key="child.id"
                      class="submenu-item"
                      :class="{ 'active': isActive(child.route) }"
                      @click="handleMenuItemClick(child)"
                    >
                      <div class="submenu-icon"><i :class="child.icon"></i></div>
                      <div class="submenu-text">
                        <span class="submenu-title">{{ child.title }}</span>
                        <span class="submenu-badge" v-if="child.badge">{{ child.badge }}</span>
                      </div>
                    </div>
                  </div>
                </transition>
              </template>
              <div
                v-else
                class="menu-item"
                :class="{ 'active': isActive(item.route), 'collapsed': !showExpanded }"
                @click="handleMenuItemClick(item)"
              >
                <div class="menu-icon"><i :class="item.icon"></i></div>
                <div class="menu-text" v-if="showExpanded">
                  <span class="menu-title">{{ item.title }}</span>
                  <span class="menu-badge" v-if="item.badge">{{ item.badge }}</span>
                </div>
                <div class="menu-tooltip" v-if="!showExpanded">{{ item.title }}</div>
              </div>
            </div>
            </div>
          </transition>
        </div>
      </div>

      <!-- Navigation Footer -->
      <div class="nav-footer" v-if="showExpanded">
        <div class="footer-content">
          <div class="footer-item" @click="toggleTheme">
            <i class="bx bx-sun"></i>
            <span>Theme</span>
          </div>
        </div>
      </div>
    </nav>

    <transition name="fade">
      <div
        v-if="isMobile && isMobileOpen"
        class="mobile-menu-backdrop"
        @click="isMobileOpen = false"
      ></div>
    </transition>

    <button
      v-if="isMobile"
      class="mobile-menu-btn"
      @click.prevent="toggleNav"
      aria-label="Toggle navigation menu"
      type="button"
    >
      <i class="bx" :class="isMobileOpen ? 'bx-x' : 'bx-menu'"></i>
    </button>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, county_logo, execute, getRight, fetchPermissionsAndMenus } from "@/api";
import fallbackLogo from "@/assets/logo.png";

export default {
  name: 'Navigation',

  data() {
    return {
      isCollapsed: false,
      isMobileOpen: false,
      isMobile: false,
      showProfileMenu: false,
      searchQuery: '',
      expandedItems: {},
      expandedModules: {},
      navLogoSrc: (process.env.BASE_URL || "/") + "assets/homabay_logo.png",
      filteredMenuSections: [],
      userTypeLabel: '',

      allMenuSections: [
        {
          title: 'Governor',
          items: [{ id: 'governor-dashboard', title: 'Governor Dashboard', icon: 'bx bx-home-circle', route: 'dashboard', permission: 'DASHBOARD' }]
        },
        {
          title: 'Dashboard',
          items: [{ id: 'dashboard', title: 'Dashboard', icon: 'bx bx-home-circle', route: 'dashboard', permission: 'DASHBOARD' }]
        },
        {
          title: 'Staff Establishment',
          items: [
            { id: 'employee-register', title: 'Employee Register', icon: 'mdi mdi-account-group', route: 'employee-register', permission: 'EMPLOYEE REGISTER' },
            { id: 'position-register', title: 'Position Register', icon: 'mdi mdi-account-details', route: 'position-register', permission: 'EMPLOYEE REGISTER' },
            { id: 'archived-employees', title: 'Archived Employees', icon: 'mdi mdi-archive', route: 'archived-employees', permission: 'EMPLOYEE REGISTER' },
            {
              id: 'employee-approvals', title: 'Employee Approvals', icon: 'mdi mdi-check-decagram', route: null, permission: 'EMPLOYEE APPROVALS',
              children: [
                { id: 'employee-approvals-new', title: 'Pending Approvals - New Employees', icon: 'mdi mdi-account-check', route: 'employee-approvals-new', permission: 'PENDING APPROVALS NEW EMPLOYEES' },
                { id: 'employee-approvals-edit-approvals', title: 'Pending Approvals - Data Edits', icon: 'mdi mdi-pencil', route: 'employee-approvals-edit-approvals', permission: 'PENDING APPROVALS DATA EDITS' },
                { id: 'employee-approvals-approved', title: 'Approved Recruits', icon: 'mdi mdi-account-check-outline', route: 'employee-approvals-approved', permission: 'EMPLOYEE APPROVALS' },
                { id: 'employee-approvals-rejected', title: 'Rejected', icon: 'mdi mdi-close-circle', route: 'employee-approvals-rejected', permission: 'REJECTED APPROVALS' }
              ]
            }
          ]
        },
        {
          title: 'County Secretary',
          items: [
            { id: 'module-cabinet', title: 'Cabinet', icon: 'mdi mdi-domain', route: 'module-cabinet', permission: ['CABINET_VIEW', 'CABINET_MANAGE'] },
            { id: 'module-hr-knowledge', title: 'Correspondences', icon: 'mdi mdi-book-open-variant', route: 'module-hr-knowledge', permission: ['HR_KNOWLEDGE_VIEW', 'HR_KNOWLEDGE_MANAGE', 'CORRESPONDENCES_VIEW'] },
            { id: 'module-cs-correspondences', title: 'Correspondences from CS', icon: 'mdi mdi-book-education-outline', route: 'module-cs-correspondences', permission: ['CS_CORRESPONDENCE_VIEW', 'CS_CORRESPONDENCE_MANAGE', 'CORRESPONDENCES_VIEW'] }
          ]
        },
        {
          title: 'Public Service Board',
          items: [
            { id: 'staff-establishment', title: 'Staff Establishment', icon: 'mdi mdi-sitemap-outline', route: 'staff-establishment', permission: 'STAFF ESTABLISHMENT' },
            { id: 'vacancy-management', title: 'Vacancy Management', icon: 'mdi mdi-briefcase-search-outline', route: 'vacancy-management', permission: 'VACANCY MANAGEMENT' },
            { id: 'applicants', title: 'Applicants', icon: 'mdi mdi-account-group-outline', route: 'applicants', permission: 'VACANCY MANAGEMENT' },
            { id: 'shortlisting-management', title: 'Shortlisting Management', icon: 'mdi mdi-account-search-outline', route: 'shortlisting-management', permission: 'SHORTLISTING MANAGEMENT' },
            { id: 'interview-management', title: 'Interview Management', icon: 'mdi mdi-account-tie-hat', route: 'interview-management', permission: 'INTERVIEW MANAGEMENT' },
            { id: 'appointment-management', title: 'Appointment Management', icon: 'mdi mdi-file-document-edit-outline', route: 'appointment-management', permission: 'APPOINTMENT MANAGEMENT' },
            { id: 'module-confirmation-probation', title: 'Confirmation & Probation', icon: 'mdi mdi-clipboard-check-outline', route: 'module-confirmation-probation', permission: ['CONFIRMATION_PROBATION_VIEW', 'CONFIRMATION_PROBATION_MANAGE'] },
            { id: 'module-acting-appointment', title: 'Acting Appointments', icon: 'mdi mdi-account-tie', route: 'module-acting-appointment', permission: ['ACTING_APPOINTMENT_VIEW', 'ACTING_APPOINTMENT_MANAGE'] },
            { id: 'module-short-term-employment', title: 'Short Term Employment', icon: 'mdi mdi-file-document-edit-outline', route: 'module-short-term-employment', permission: 'SHORT TERM EMPLOYMENT' },
            { id: 'disciplinary-hr-actions', title: 'Disciplinary & HR Action', icon: 'mdi mdi-gavel', route: 'disciplinary-hr-actions', permission: 'DISCIPLINARY_HR_ACTIONS' },
            { id: 'retirement-management', title: 'Retirement Management', icon: 'mdi mdi-account-clock', route: 'retirement-management', permission: 'RETIREMENT_MANAGEMENT' },
            { id: 'module-board-decisions', title: 'Board Decisions', icon: 'mdi mdi-gavel', route: 'module-board-decisions', permission: ['BOARD_DECISION_VIEW', 'BOARD_DECISION_MANAGE'] },
            { id: 'wealth-declaration-approved', title: 'Wealth-Declaration-Approved', icon: 'mdi mdi-file-check', route: 'wealth-declaration-approved', permission: ['WEALTH_DECLARATION_REPORTS', 'REPORT_WEALTH_DECLARATION'] }
          ]
        },
        {
          title: 'County Attorney',
          items: [
            { id: 'county-attorney-documents', title: 'County Attorney', icon: 'mdi mdi-scale-balance', route: 'county-attorney-documents', permission: 'COUNTY ATTORNEY' },
            { id: 'incoming-attorney-documents', title: 'Incoming Documents', icon: 'mdi mdi-inbox-arrow-down', route: 'incoming-attorney-documents', permission: ['INCOMING ATTORNEY DOCUMENTS', 'COUNTY ATTORNEY'] }
          ]
        },
        {
          title: 'Attendance Management',
          items: [
            { id: 'attendance-timesheet', title: 'Timesheet', icon: 'mdi mdi-clock-time-four', route: 'attendance-timesheet', permission: 'TIMESHEET' },
            { id: 'shift-management', title: 'Shift Management', icon: 'mdi mdi-clock-time-eight-outline', route: 'shift-management', permission: 'SHIFT MANAGEMENT' }
          ]
        },
        {
          title: 'Leave Management',
          items: [
            { id: 'attendance-leave', title: 'Leave Approval', icon: 'mdi mdi-calendar', route: 'attendance-leave', permission: 'MANAGE LEAVE' },
            { id: 'attendance-leave-manage', title: 'Leave Balances', icon: 'mdi mdi-calendar-remove', route: 'attendance-leave-manage', permission: 'MANAGE LEAVE' },
            { id: 'leave-types', title: 'Leave Types', icon: 'mdi mdi-calendar-remove', route: 'leave-types', permission: 'MANAGE LEAVE' },
            { id: 'off-days-management', title: 'Off Days', icon: 'mdi mdi-beach', route: 'off-days-management', permission: ['MANAGE LEAVE', 'MANAGE OFF DAYS'] },
            { id: 'off-day-approvals', title: 'Off Day Approvals', icon: 'mdi mdi-calendar-check', route: 'off-day-approvals', permission: ['MANAGE LEAVE', 'MANAGE OFF DAYS'] },
            { id: 'leave-planning', title: 'Leave Planning', icon: 'mdi mdi-calendar-month', route: 'leave-planning', permission: 'LEAVE_PLANNING' },
            { id: 'unused-leave-approvals', title: 'Unused Leave Approvals', icon: 'mdi mdi-calendar-check', route: 'unused-leave-approvals', permission: ['UNUSED LEAVE APPROVE DIRECTOR HR', 'UNUSED LEAVE APPROVE WELFARE', 'UNUSED LEAVE ADMIN', 'MANAGE LEAVE'] }
          ]
        },
        {
          title: 'Document Management',
          items: [
            { id: 'file-registry-docs', title: 'Record Management', icon: 'mdi mdi-folder-account', route: 'file-registry', permission: 'FILE REGISTRY' },
            { id: 'documents-general', title: 'General Documents', icon: 'mdi mdi-file', route: 'documents-general', permission: 'GENERAL DOCUMENTS' },
            { id: 'documents-from-attorney', title: 'Documents From Attorney', icon: 'mdi mdi-scale-balance', route: 'documents-from-attorney', permission: ['DOCUMENTS FROM ATTORNEY', 'COUNTY ATTORNEY'] }
          ]
        },
        {
          title: 'HR Actions',
          items: [
            { id: 'attrition', title: 'Attrition', icon: 'mdi mdi-account-arrow-right', route: 'attrition', permission: ['ATTRITION_VIEW', 'ATTRITION_CREATE', 'REPORT_ATTRITION'] },
            { id: 'module-compliance-unit', title: 'Compliance Unit', icon: 'mdi mdi-shield-alert', route: 'module-compliance-unit', permission: ['COMPLIANCE_UNIT_VIEW', 'COMPLIANCE_UNIT_MANAGE'] },
            { id: 'department-management', title: 'Departments', icon: 'mdi mdi-account-multiple', route: 'department-management', permission: 'DEPARTMENT MANAGEMENT' },
            {
              id: 'separation', title: 'Workforce Movement', icon: 'mdi mdi-logout-variant', route: null, permission: 'SEPARATION',
              children: [
                { id: 'separation-termination', title: 'Movements', icon: 'mdi mdi-swap-horizontal', route: 'separation-termination', permission: 'TERMINATION' },
                { id: 'separation-fnf', title: 'FNF', icon: 'mdi mdi-cash', route: 'separation-fnf', permission: 'FNF' }
              ]
            },
            { id: 'module-redesignation', title: 'Redesignation', icon: 'mdi mdi-swap-horizontal', route: 'module-redesignation', permission: ['REDESIGNATION_VIEW', 'REDESIGNATION_MANAGE'] },
            { id: 'module-redeployment', title: 'Redeployment', icon: 'mdi mdi-swap-horizontal-bold', route: 'module-redeployment', permission: ['REDEPLOYMENT_VIEW', 'REDEPLOYMENT_MANAGE'] },
            { id: 'module-redeployment-plan', title: 'Redeployment Plan', icon: 'mdi mdi-clipboard-text-outline', route: 'module-redeployment-plan', permission: ['REDEPLOYMENT_PLAN_CREATE', 'REDEPLOYMENT_MANAGE', 'REDEPLOYMENT_VIEW'] },
            { id: 'employee-certifications', title: 'Employee Certifications', icon: 'bx bx-certification', route: 'employee-certifications', permission: 'EMPLOYEE CERTIFICATIONS' },
            { id: 'wealth-declaration-approvals', title: 'Wealth-Declaration Approvals', icon: 'mdi mdi-file-document-check', route: 'wealth-declaration-approvals', permission: 'WEALTH_DECLARATION' },
            { id: 'announcements', title: 'Announcements', icon: 'mdi mdi-bullhorn-outline', route: 'announcements', permission: 'ANNOUNCEMENTS' },
            { id: 'hr-events', title: 'Events', icon: 'mdi mdi-calendar-star', route: 'hr-events', permission: 'EVENTS' },
            { id: 'hr-meetings', title: 'Meetings', icon: 'mdi mdi-account-group-outline', route: 'hr-meetings', permission: 'MEETINGS' },
            { id: 'hr-transfers', title: 'Transfers', icon: 'mdi mdi-transfer', route: 'hr-transfers', permission: ['TRANSFERS', 'TRANSFERS_INITIATE', 'TRANSFERS_APPROVE_SOURCE', 'TRANSFERS_APPROVE_DESTINATION', 'TRANSFERS_RECEIVE'] },
            { id: 'hr-warnings', title: 'Warnings', icon: 'mdi mdi-bullhorn', route: 'hr-warnings', permission: 'WARNINGS' },
            { id: 'trainings-catalog', title: 'Trainings Catalog', icon: 'bx bx-book', route: 'trainings-catalog', permission: 'TRAININGS CATALOG' },
            { id: 'training-management', title: 'Training Management', icon: 'mdi mdi-school', route: 'training-management', permission: 'TRAINING MANAGEMENT' },
            { id: 'training-calendar', title: 'Training Calendar', icon: 'mdi mdi-calendar-star', route: 'training-calendar', permission: 'TRAINING CALENDAR' },
            { id: 'training-evaluations', title: 'Training Evalutions', icon: 'mdi mdi-file-document-edit-outline', route: 'training-evaluations', permission: 'TRAINING EVALUATIONS' },
            {
              id: 'holidays-section', title: 'Holidays', icon: 'mdi mdi-beach', route: '/holidays', permission: 'HOLIDAYS',
              children: [
                { id: 'holidays', title: 'Manage Holidays', icon: 'mdi mdi-calendar-check', route: '/holidays', permission: 'MANAGE HOLIDAYS' },
                { id: 'holiday-categories', title: 'Categories', icon: 'mdi mdi-tag-multiple', route: '/holiday-categories', permission: 'HOLIDAY CATEGORIES' },
                { id: 'holiday-calendar', title: 'Calendar', icon: 'mdi mdi-calendar-month', route: '/holiday-calendar', permission: 'HOLIDAY CALENDAR' },
                { id: 'holiday-statistics', title: 'Statistics', icon: 'mdi mdi-chart-bar', route: '/holiday-statistics', permission: 'HOLIDAY STATISTICS' }
              ]
            }
          ]
        },
        {
          title: 'Skills Audit',
          items: [
            { id: 'module-employees-skills-audits', title: 'Employees Skills Audits', icon: 'mdi mdi-account-school-outline', route: 'module-employees-skills-audits', permission: 'EMPLOYEES SKILLS AUDITS' },
            { id: 'module-skills-audit-me', title: 'Monitoring and Evaluation', icon: 'mdi mdi-monitor-dashboard', route: 'module-skills-audit-me', permission: 'SKILLS AUDIT MONITORING EVALUATION' },
            { id: 'module-skills-audit-skill-gap', title: 'Skill Gap', icon: 'mdi mdi-chart-timeline-variant', route: 'module-skills-audit-skill-gap', permission: 'SKILLS AUDIT SKILL GAP' },
            { id: 'module-skills-audit-inventory', title: 'Skills Inventory', icon: 'mdi mdi-table-large', route: 'module-skills-audit-inventory', permission: 'SKILLS AUDIT SKILLS INVENTORY' },
            { id: 'skills-audit-report', title: 'Skills Audit Report', icon: 'mdi mdi-clipboard-text-search-outline', route: 'skills-audit-report', permission: 'SKILLS AUDIT REPORT' }
          ]
        },
        {
          title: 'Finance',
          items: [
            {
              id: 'imprest', title: 'Imprest', icon: 'mdi mdi-cash', route: null, permission: 'IMPREST',
              children: [
                { id: 'imprest-request', title: 'Imprest Request', icon: 'mdi mdi-plus-circle', route: 'imprest-request', permission: 'IMPREST REQUEST' },
                { id: 'imprest-approval', title: 'Imprest Approval', icon: 'mdi mdi-check-circle', route: 'imprest-approval', permission: 'IMPREST APPROVAL' },
                { id: 'imprest-reconciliation', title: 'Imprest Recon', icon: 'mdi mdi-file-compare', route: 'imprest-reconciliation', permission: 'IMPREST RECONCILIATION' },
                { id: 'imprest-history', title: 'Imprest History', icon: 'mdi mdi-history', route: 'imprest-history', permission: 'IMPREST HISTORY' }
              ]
            }
          ]
        },
        {
          title: 'Reports',
          items: [
            { id: 'report-staff-establishment', title: 'Staff Establishment', icon: 'mdi mdi-account-group', route: 'report-staff-establishment', permission: 'REPORT_STAFF_ESTABLISHMENT' },
            { id: 'report-attrition', title: 'Attrition Report', icon: 'mdi mdi-account-arrow-right', route: 'report-attrition', permission: 'REPORT_ATTRITION' },
            { id: 'report-wealth-compliance', title: 'Wealth Declaration Compliance', icon: 'mdi mdi-file-check', route: 'report-wealth-compliance', permission: 'REPORT_WEALTH_DECLARATION' },
            { id: 'report-wealth-non-compliant', title: 'Wealth Non-Compliant Officers', icon: 'mdi mdi-file-alert', route: 'report-wealth-non-compliant', permission: 'REPORT_WEALTH_DECLARATION' },
            { id: 'report-department-wage-bill', title: 'Department Wage Bill', icon: 'mdi mdi-cash-multiple', route: 'report-department-wage-bill', permission: 'REPORT_DEPARTMENT_WAGE_BILL' },
            { id: 'report-county-wage-bill', title: 'County Wage Bill', icon: 'mdi mdi-bank', route: 'report-county-wage-bill', permission: 'REPORT_COUNTY_WAGE_BILL' },
            { id: 'payroll-csv-reconciliation', title: 'Payroll CSV vs Register', icon: 'mdi mdi-file-upload-outline', route: 'payroll-csv-reconciliation', permission: ['REPORT_DEPARTMENT_WAGE_BILL', 'PAYROLL REPORT', 'UPLOAD PAYROLL', 'REPORT_COUNTY_WAGE_BILL'] },
            { id: 'report-daily', title: 'Daily Attendance', icon: 'mdi mdi-calendar-today', route: 'report-daily', permission: 'DAILY ATTENDANCE' },
            { id: 'report-monthly-r', title: 'Monthly Attendance', icon: 'mdi mdi-calendar-month', route: 'report-monthly', permission: 'MONTHLY ATTENDANCE' },
            { id: 'report-monthly-detailed', title: 'Detailed Monthly Report', icon: 'mdi mdi-file-document-edit-outline', route: 'report-monthly-detailed', permission: 'DETAILED MONTHLY ATTENDANCE' },
            { id: 'report-hr-report', title: 'Dept. HR Report', icon: 'mdi mdi-account-group', route: 'report-hr-report', permission: 'HR REPORT' },
            { id: 'report-leave', title: 'Leave Report', icon: 'mdi mdi-airplane-off', route: 'report-leave', permission: 'LEAVE REPORT' },
            { id: 'report-timesheet', title: 'Timesheet Report', icon: 'mdi mdi-clock-time-eight-outline', route: 'report-timesheet', permission: 'TIMESHEET REPORT' },
            { id: 'report-awol', title: 'AWOL Report', icon: 'mdi mdi-account-off', route: 'report-awol', permission: 'AWOL REPORT' },
            { id: 'off-duty-details', title: 'Unapproved Leave (15+ Days)', icon: 'mdi mdi-account-alert', route: 'off-duty-details', permission: 'OFF DUTY DETAILS' }
          ]
        },
        {
          title: 'System Management',
          items: [
            { id: 'org-structure', title: 'Organization Structure', icon: 'mdi mdi-family-tree', route: 'org-structure', permission: ['ORG STRUCTURE', 'STAFF ESTABLISHMENT'] },
            { id: 'position-management', title: 'Designation Management', icon: 'mdi mdi-shield-account', route: 'position-management', permission: ['POSITION MANAGEMENT', 'STAFF ESTABLISHMENT'] },
            { id: 'category-role-management', title: 'Access profiles & roles', icon: 'mdi mdi-account-key', route: 'category-role-management', permission: 'CATEGORY & ROLES' },
            { id: 'payroll-setup', title: 'Setup & Configuration', icon: 'mdi mdi-cog-outline', route: 'payroll-setup', permission: 'PAYROLL SETUP' },
            { id: 'module-employment-types', title: 'Employment Types', icon: 'mdi mdi-briefcase-edit-outline', route: 'module-employment-types', permission: ['EMPLOYMENT_TYPE_VIEW', 'EMPLOYMENT_TYPE_MANAGE'] },
            { id: 'user-management', title: 'User Management', icon: 'mdi mdi-account-cog', route: 'user-management', permission: 'USER MANAGEMENT' },
            { id: 'audit-logs', title: 'Audit Logs', icon: 'mdi mdi-history', route: 'audit-logs', permission: ['USER MANAGEMENT', 'AUDIT LOGS'] }
          ]
        },
        {
          title: 'Employee Self-service',
          items: [
            { id: 'employee-dashboard', title: 'My Dashboard', icon: 'mdi mdi-view-dashboard', route: 'employee-dashboard', permission: 'MY DASHBOARD' },
            { id: 'employee-profile', title: 'My Profile', icon: 'mdi mdi-account', route: 'employee-profile', permission: 'MY PROFILE' },
            { id: 'my-position-info', title: 'My Position Info', icon: 'mdi mdi-account-tie', route: 'my-position-info', permission: 'MY PROFILE' },
            { id: 'employee-announcements', title: 'My Announcements', icon: 'mdi mdi-bullhorn', route: 'employee-announcements', permission: 'MY ANNOUNCEMENTS' },
            {
              id: 'employee-attendance', title: 'My Attendance', icon: 'mdi mdi-clock-check-outline', route: null, permission: 'MY ATTENDANCE',
              children: [
                { id: 'employee-attendance-records', title: 'Attendance Records', icon: 'mdi mdi-clipboard-text', route: 'employee-attendance-records', permission: 'ATTENDANCE RECORDS' },
                { id: 'employee-attendance-clock', title: 'Clock In/Out', icon: 'mdi mdi-clock-in', route: 'employee-attendance-clock', permission: 'CLOCK IN OUT' },
                { id: 'employee-attendance-summary', title: 'Attendance Summary', icon: 'mdi mdi-chart-bar', route: 'employee-attendance-summary', permission: 'ATTENDANCE SUMMARY' }
              ]
            },
            { id: 'employee-my-events', title: 'My Events', icon: 'mdi mdi-calendar', route: 'hr-my-events', permission: 'MY EVENTS' },
            {
              id: 'employee-leave', title: 'My Leave', icon: 'mdi mdi-calendar-month', route: null, permission: 'MY LEAVE',
              children: [
                { id: 'employee-leave-apply', title: 'Apply for Leave', icon: 'mdi mdi-plus-circle', route: 'employee-leave-apply', permission: 'APPLY FOR LEAVE' },
                { id: 'employee-off-day-apply', title: 'Apply for Off Day', icon: 'mdi mdi-beach', route: 'employee-off-day-apply', permission: ['APPLY FOR LEAVE', 'APPLY OFF DAY', 'MY LEAVE'] },
                { id: 'employee-off-day-history', title: 'My Off Days', icon: 'mdi mdi-calendar-star', route: 'employee-off-day-history', permission: ['LEAVE HISTORY', 'OFF DAY HISTORY', 'MY LEAVE'] },
                { id: 'employee-leave-history', title: 'Leave History', icon: 'mdi mdi-history', route: 'employee-leave-history', permission: 'LEAVE HISTORY' },
                { id: 'employee-leave-balance', title: 'Leave Balance', icon: 'mdi mdi-chart-donut', route: 'employee-leave-balance', permission: 'LEAVE BALANCE' },
                { id: 'employee-leave-calendar', title: 'Leave Calendar', icon: 'mdi mdi-calendar', route: 'employee-leave-calendar', permission: 'LEAVE CALENDAR' },
                { id: 'employee-unused-leave-submit', title: 'Submit Unused Leave', icon: 'mdi mdi-calendar-refresh', route: 'employee-unused-leave-submit', permission: ['SUBMIT UNUSED LEAVE', 'APPLY FOR LEAVE'] }
              ]
            },
            { id: 'employee-my-meetings', title: 'My Meetings', icon: 'mdi mdi-account-voice', route: 'employee-my-meetings', permission: 'MY MEETINGS' },
            {
              id: 'employee-performance', title: 'My Performance', icon: 'mdi mdi-trophy', route: null, permission: 'MY PERFORMANCE',
              children: [
                { id: 'my-appraisals-ess', title: 'My Appraisals', icon: 'mdi mdi-file-chart', route: 'my-appraisals', permission: 'MY APPRAISALS' },
                { id: 'employee-self-assessment-ess', title: 'Self Assessment', icon: 'mdi mdi-clipboard-edit', route: 'employee-self-assessment', permission: 'SELF ASSESSMENT' }
              ]
            },
            { id: 'my-redeployment', title: 'My Redeployment', icon: 'mdi mdi-swap-horizontal-bold', route: 'my-redeployment', permission: ['REDEPLOYMENT_EMPLOYEE_ACK', 'REDEPLOYMENT_EMPLOYEE_FEEDBACK'] },
            { id: 'my-skills-audit', title: 'My Skills Audit', icon: 'mdi mdi-clipboard-edit-outline', route: 'my-skills-audit', permission: 'MY SKILLS AUDIT' },
            { id: 'my-training', title: 'My Training', icon: 'mdi mdi-school', route: 'my-training', permission: 'MY TRAINING' },
            { id: 'employee-my-warnings', title: 'My Warnings', icon: 'mdi mdi-bullhorn', route: 'employee-my-warnings', permission: 'MY WARNINGS' },
            { id: 'employee-coia-declaration', title: 'My Wealth Declaration', icon: 'mdi mdi-file-sign', route: 'employee-coia-declaration', permission: ['MY_COIA_DECLARATION', 'MY PROFILE'] },
            { id: 'skills-audit-assessment', title: 'Skills Audit Assessment', icon: 'mdi mdi-clipboard-file-outline', route: 'skills-audit-assessment', permission: 'MY SKILLS AUDIT ASSESSMENT' },
            {
              id: 'employee-documents', title: 'My Documents', icon: 'mdi mdi-folder-account', route: null, permission: 'MY DOCUMENTS',
              children: [
                { id: 'employee-documents-view', title: 'View Documents', icon: 'mdi mdi-file-eye', route: 'employee-documents-view', permission: 'VIEW DOCUMENTS' },
                { id: 'employee-documents-upload', title: 'Upload Documents', icon: 'mdi mdi-file-upload', route: 'employee-documents-upload', permission: 'UPLOAD DOCUMENTS' }
              ]
            },
            {
              id: 'employee-payroll', title: 'My Payroll', icon: 'mdi mdi-cash-multiple', route: null, permission: 'MY PAYROLL',
              children: [
                { id: 'employee-payroll-payslips', title: 'View Payslips', icon: 'mdi mdi-file-document', route: 'employee-payroll-payslips', permission: 'VIEW PAYSLIPS' },
                { id: 'employee-payroll-tax', title: 'Tax Documents', icon: 'mdi mdi-file-certificate', route: 'employee-payroll-tax', permission: 'TAX DOCUMENTS' },
                { id: 'employee-payroll-history', title: 'Payroll History', icon: 'mdi mdi-history', route: 'employee-payroll-history', permission: 'PAYROLL HISTORY' }
              ]
            }
          ]
        }
      ],

      sessionNames: '',
      county_logo: county_logo,

      // Canonical order only. Legacy DB titles are folded via resolveMenuSection(); sort uses ranks so System → ESS stay last.
      SECTION_ORDER: [
        'Governor',
        'Dashboard',
        'County Secretary',
        'Staff Establishment',
        'County Attorney',
        'Public Service Board',
        'Attendance Management',
        'Leave Management',
        'Document Management',
        'HR Actions',
        'Skills Audit',
        'Finance',
        'Reports',
        'System Management',
        'Employee Self-service'
      ]
    };
  },

  computed: {
    currentRoute() { return this.$route.name; },
    /**
     * Whether to show the "expanded" UI inside the nav (brand text, profile
     * card with Logout, search box, menu labels, footer).
     *
     * On desktop this just mirrors `!isCollapsed`.
     * On mobile we always force `isCollapsed = true` (to keep the desktop
     * icon-only state machine simple), but when the drawer slides in we
     * still want to show the full expanded contents — otherwise the user
     * has no Logout button, no search, and unlabelled menu icons.
     */
    showExpanded() {
      return !this.isCollapsed || (this.isMobile && this.isMobileOpen);
    }
  },

  methods: {
    /**
     * Must be methods (not computed): sessionStorage is not reactive — computeds cached false before RBAC/login roles load.
     * Governor = users.category / access profile "Governor" (or containing word Governor), OR role names, OR designation.
     */
    roleRowLabel(r) {
      if (!r || typeof r !== "object") return "";
      return String(
        r.role_name ?? r.roleName ?? r.Role_Name ?? r.ROLE_NAME ?? r.name ?? ""
      ).trim();
    },
    stringLooksLikeGovernorRole(s) {
      const t = String(s || "").trim();
      if (!t) return false;
      const u = t.toUpperCase();
      const norm = u.replace(/[^A-Z0-9]/g, "");
      if (norm === "GOVERNOR") return true;
      if (u === "GOVERNOR") return true;
      return /\bGOVERNOR\b/i.test(t);
    },
    sessionIsGovernorUser() {
      try {
        const ctxRaw = sessionStorage.getItem("rbac_context");
        if (ctxRaw) {
          const ctx = JSON.parse(ctxRaw);
          if (this.stringLooksLikeGovernorRole(ctx?.user_category)) return true;
          const names = ctx?.role_names;
          if (Array.isArray(names) && names.some((n) => this.stringLooksLikeGovernorRole(n))) return true;
        }
      } catch (_) {}
      try {
        const rbacStr = sessionStorage.getItem("rbac_permissions");
        if (rbacStr) {
          const perms = JSON.parse(rbacStr);
          if (Array.isArray(perms) && perms.some((p) => this.stringLooksLikeGovernorRole(p))) return true;
        }
      } catch (_) {}
      try {
        const rolesRaw = sessionStorage.getItem("selectedRoles");
        const roles = rolesRaw ? JSON.parse(rolesRaw) : [];
        if (Array.isArray(roles) && roles.some((r) => this.stringLooksLikeGovernorRole(this.roleRowLabel(r)))) return true;
      } catch (_) {}
      try {
        const raw = sessionStorage.getItem("userInfo");
        const u = raw ? JSON.parse(raw) : {};
        if (this.stringLooksLikeGovernorRole(u.category || u.Category)) return true;
        const des = String(u.designation || u.positionTitle || u.position_title || "").trim().toLowerCase();
        if (des === "governor" || des === "executive") return true;
      } catch (_) {}
      return false;
    },
    stringLooksLikeHeadOfHr(s) {
      const u = String(s || "").trim().toUpperCase();
      return u === "HEAD OF HR" || u.includes("HEAD OF HR");
    },
    sessionIsHeadOfHrUser() {
      try {
        const ctxRaw = sessionStorage.getItem("rbac_context");
        if (ctxRaw) {
          const ctx = JSON.parse(ctxRaw);
          const names = ctx?.role_names;
          if (Array.isArray(names) && names.some((n) => this.stringLooksLikeHeadOfHr(n))) return true;
        }
      } catch (_) {}
      try {
        const rbacStr = sessionStorage.getItem("rbac_permissions");
        if (rbacStr) {
          const perms = JSON.parse(rbacStr);
          if (Array.isArray(perms) && perms.some((p) => this.stringLooksLikeHeadOfHr(p))) return true;
        }
      } catch (_) {}
      try {
        const rolesRaw = sessionStorage.getItem("selectedRoles");
        const roles = rolesRaw ? JSON.parse(rolesRaw) : [];
        if (Array.isArray(roles) && roles.some((r) => this.stringLooksLikeHeadOfHr(this.roleRowLabel(r)))) return true;
      } catch (_) {}
      return false;
    },
    sessionShowGovernorDashboardNavOnly() {
      return this.sessionIsGovernorUser() && !this.sessionIsHeadOfHrUser();
    },
    toggleNav() {
      if (this.isMobile) {
        this.isMobileOpen = !this.isMobileOpen;
      } else {
        this.isCollapsed = !this.isCollapsed;
      }
      this.$emit('nav-toggle', this.isCollapsed || this.isMobileOpen);
      if ((this.isMobile && !this.isMobileOpen) || (!this.isMobile && this.isCollapsed)) {
        this.expandedItems = {};
      }
    },

    toggleProfileMenu() { this.showProfileMenu = !this.showProfileMenu; },

    toggleSubmenu(item) {
      const newExpandedItems = { ...this.expandedItems };
      newExpandedItems[item.id] = !newExpandedItems[item.id];
      if (newExpandedItems[item.id]) {
        Object.keys(newExpandedItems).forEach(key => { if (key !== item.id) newExpandedItems[key] = false; });
      }
      this.expandedItems = newExpandedItems;
      // Do not scroll here: opening/closing a dropdown should keep the user's scroll position
      // in the sidebar (scroll-to-active runs on route change only).
    },

    handleParentWithChildrenClick(item) {
      if (item.route) this.goTo(item.route);
      this.toggleSubmenu(item);
    },

    handleMenuItemClick(item) {
      if (item.external) { window.open(item.external, '_blank'); return; }
      if (item.route) {
        this.goTo(item.route);
        this.showProfileMenu = false;
        if (this.isMobile) this.isMobileOpen = false;
        this.expandedItems = {};
      }
    },

    isActive(route) {
      if (!route) return false;
      return this.currentRoute === route || this.$route.path === route;
    },

    isActiveParent(item) {
      if (!item.children) return false;
      return item.children.some(child => this.isActive(child.route));
    },

    onNavLogoError() {
      this.navLogoSrc = fallbackLogo;
    },

    /** Nav section headings: canonical keys stay in data/RBAC; labels match Finance-style groups. */
    sectionDisplayTitle(title) {
      const map = {
        "Public Service Board": "Public Service Module",
        "Leave Management": "Leave Management Module",
        "HR Actions": "HR Actions Module",
        "Skills Audit": "Skills Audit Module",
        Reports: "Reports Module"
      };
      const k = String(title || "").trim();
      return map[k] || title;
    },

    isCollapsibleModuleSection(title) {
      const t = String(title || "").trim();
      return [
        "Public Service Board",
        "Leave Management",
        "HR Actions",
        "Skills Audit",
        "Reports"
      ].includes(t);
    },

    sectionModuleKey(title) {
      return String(title || "").trim().replace(/\s+/g, "_");
    },

    toggleModuleSection(key) {
      const cur = !!this.expandedModules[key];
      this.expandedModules = { ...this.expandedModules, [key]: !cur };
    },

    isModuleSectionExpanded(section) {
      return !!this.expandedModules[this.sectionModuleKey(section.title)];
    },

    moduleSectionHasActiveRoute(section) {
      return (section.items || []).some((item) => {
        if (item.children?.length) {
          return item.children.some((ch) => this.filterLeafForNav(ch) && this.isActive(ch.route));
        }
        return this.filterLeafForNav(item) && this.isActive(item.route);
      });
    },

    expandActiveModuleSections() {
      const sections = this.getMenuSections();
      sections.forEach((section) => {
        if (!this.isCollapsibleModuleSection(section.title)) return;
        const items = (section.items || []).map((item) => this.cloneItemVisible(item)).filter(Boolean);
        if (items.length && this.moduleSectionHasActiveRoute({ ...section, items })) {
          const k = this.sectionModuleKey(section.title);
          this.expandedModules = { ...this.expandedModules, [k]: true };
        }
      });
    },

    goTo(route) {
      if (route) {
        const path = route.startsWith('/') || route.startsWith('http') ? route : '/' + route;
        sessionStorage.setItem("selected", route);
        sessionStorage.setItem("selectedSub", route);
        sessionStorage.setItem("currentPage", route);
        this.$router.push(path).then(() => { this.scrollToActiveMenuItem(); });
        this.showProfileMenu = false;
      }
    },

    hasPermission(permission) {
      if (!permission) return true;
      // For sidebar visibility, prefer server RBAC permissions over legacy selectedRoles fallback.
      // This avoids stale menu items remaining visible after role deselection.
      try {
        const raw = sessionStorage.getItem("rbac_permissions");
        const perms = raw ? JSON.parse(raw) : [];
        if (Array.isArray(perms) && perms.length) {
          const normalize = (s) => String(s || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
          const wanted = (Array.isArray(permission) ? permission : [permission]).map(normalize);
          const have = new Set(perms.map(normalize));
          return wanted.some((w) => have.has(w));
        }
      } catch (_) {}
      return getRight(permission);
    },

    /** Sidebar visibility = permission-based; nav_menus can explicitly disable/enable matching routes. */
    filterLeafForNav(item) {
      if (!item) return false;
      if (item.route) {
        // Critical system routes should remain permission-driven even if nav_menus is stale.
        if (this.isCriticalPermissionRoute(item.route)) {
          return this.hasPermission(item.permission);
        }
        if (this.hasRouteConfigInDb(item.route) && !this.isRouteEnabledByDb(item.route)) return false;
        if (this.isRouteDisabledByDb(item.route)) return false;
      }
      return this.hasPermission(item.permission);
    },

    isCriticalPermissionRoute(route) {
      const r = this.normalizeMenuRoute(route);
      return [
        "position-management",
        "category-role-management",
        "user-management",
        "audit-logs",
        "module-employment-types",
        "announcements",
        "hr-events",
        "hr-meetings"
      ].includes(r);
    },

    getDbMenus() {
      try {
        const menusStr = sessionStorage.getItem("rbac_menus");
        if (!menusStr) return [];
        const menus = JSON.parse(menusStr);
        return Array.isArray(menus) ? menus : [];
      } catch (_) {
        return [];
      }
    },

    hasRouteConfigInDb(route) {
      const nr = this.normalizeMenuRoute(route);
      if (!nr) return false;
      const menus = this.getDbMenus();
      return menus.some((m) => this.normalizeMenuRoute(m.route) === nr);
    },

    isRouteDisabledByDb(route) {
      const nr = this.normalizeMenuRoute(route);
      if (!nr) return false;
      const menus = this.getDbMenus();
      const row = menus.find((m) => this.normalizeMenuRoute(m.route) === nr);
      if (!row) return false;
      const raw = row.is_active;
      if (raw === false || raw === 0) return true;
      if (raw == null) return false;
      return String(raw).trim() === '0';
    },

    isRouteEnabledByDb(route) {
      const nr = this.normalizeMenuRoute(route);
      if (!nr) return false;
      const menus = this.getDbMenus();
      const matches = menus.filter((m) => this.normalizeMenuRoute(m.route) === nr);
      if (!matches.length) return false;
      return matches.some((m) => !(m.is_active === false || m.is_active === 0 || String(m.is_active).trim() === '0'));
    },

    /** Visible copy of item with children pruned to allowed leaves only. */
    cloneItemVisible(item) {
      if (item.children && item.children.length > 0) {
        const kids = item.children.filter((ch) => this.filterLeafForNav(ch)).map((ch) => ({ ...ch }));
        if (kids.length === 0) return null;
        return { ...item, children: kids };
      }
      return this.filterLeafForNav(item) ? { ...item } : null;
    },

    narrowItemBySearch(item, q) {
      if (!q) return item;
      const titleHit = (item.title || '').toLowerCase().includes(q);
      if (item.children && item.children.length) {
        const matchingKids = item.children.filter((ch) => (ch.title || '').toLowerCase().includes(q));
        if (titleHit) return item;
        if (matchingKids.length) return { ...item, children: matchingKids };
        return null;
      }
      return titleHit ? item : null;
    },

    /** Stable key for matching DB section_title variants (spacing, &, and/and). */
    normalizeSectionKey(title) {
      return String(title || '')
        .trim()
        .toLowerCase()
        .replace(/&/g, 'and')
        .replace(/\s+/g, ' ');
    },

    /** Map legacy nav_menus.section_title strings into canonical groups. */
    legacySectionToCanonical(key) {
      const m = {
        'welfare unit': 'HR Actions',
        'registry unit': 'Staff Establishment',
        communications: 'County Secretary',
        communication: 'County Secretary',
        'document management': 'Document Management',
        documents: 'Document Management',
        'general hr action': 'HR Actions',
        'general hr actions': 'HR Actions',
        'hr actions': 'HR Actions',
        'public service board module': 'Public Service Board',
        'public service board': 'Public Service Board',
        'pension and benefits': 'HR Actions',
        'pension benefits': 'HR Actions',
        'leave and planning': 'Leave Management',
        'leave planning': 'Leave Management',
        'claims and payments': 'Finance',
        'claims management': 'Finance',
        'finance module': 'Finance',
        finance: 'Finance',
        'board and compliance': 'Staff Establishment',
        'board compliance': 'Staff Establishment',
        'training and development': 'HR Actions',
        'performance and appraisals': 'HR Actions',
        'asset management': 'HR Actions',
        'financial management': 'Finance',
        'hr policies and circulars': 'HR Actions',
        'reports and analytics': 'Reports',
        'retirement and exit': 'Staff Establishment',
        'employee self-service': 'Employee Self-service',
        'employee self service': 'Employee Self-service',
        'skills audit module': 'Skills Audit',
        'skills audit': 'Skills Audit'
      };
      return m[key] || null;
    },

    /** County HRMIS (SR) menus in DB share one section; place each route in the correct group. */
    sectionForCountyHrmisRoute(route) {
      const r = String(route || '');
      if (/vacancy-management|applicants|shortlisting-management|interview-management|appointment-management|staff-establishment/.test(r)) return 'Public Service Board';
      if (/module-confirmation-probation|module-acting-appointment/.test(r)) return 'Public Service Board';
      if (r.includes('module-cabinet')) return 'County Secretary';
      if (r.includes('module-board-decisions')) return 'Public Service Board';
      if (r.includes('module-hr-knowledge') || r.includes('module-cs-correspondences')) return 'County Secretary';
      if (/module-(redesignation|redeployment|compliance-unit|education-hr)/.test(r)) return 'HR Actions';
      if (/module-(employees-skills-audits|skills-audit-)/.test(r) || r.indexOf('skills-audit-report') !== -1) return 'Skills Audit';
      return 'Staff Establishment';
    },

    normalizeSectionTitle(title) {
      const raw = String(title || '').trim();
      const key = this.normalizeSectionKey(raw);
      const mapped = this.legacySectionToCanonical(key);
      if (mapped) return mapped;
      if (raw === 'Public Service Board') return 'Public Service Board';
      if (raw === 'Cabinet') return 'County Secretary';
      if (raw === 'Finance Module') return 'Finance';
      if (raw === 'Employee Self-Service') return 'Employee Self-service';
      if (raw === 'Skills Audit Module') return 'Skills Audit';
      return raw;
    },

    /** One canonical section per nav_menus row (for RBAC merge). */
    resolveMenuSection(menu) {
      const raw = String(menu.section_title || 'Menu').trim();
      const key = this.normalizeSectionKey(raw);
      if ((key.startsWith('county hrmis') && key.includes('sr')) || raw === 'County HRMIS (SR)') {
        return this.sectionForCountyHrmisRoute(menu.route);
      }
      const exact = {
        'Welfare Unit': 'HR Actions',
        'Registry Unit': 'Staff Establishment',
        Communications: 'County Secretary',
        'General HR Action': 'HR Actions',
        'Public Service Board Module': 'Public Service Board'
      };
      if (exact[raw]) return exact[raw];
      return this.normalizeSectionTitle(raw);
    },

    /** Sort: canonical groups in order; anything else before System; System second-last; ESS last. */
    applySectionOrder(sections) {
      const main = this.SECTION_ORDER || [];
      const rank = (title) => {
        if (title === 'Employee Self-service') return 1002;
        if (title === 'System Management') return 1001;
        const i = main.indexOf(title);
        if (i >= 0) return i;
        return 999;
      };
      return [...sections].sort((a, b) => rank(a.title) - rank(b.title) || String(a.title).localeCompare(String(b.title)));
    },

    deepCloneMenuSections(sections) {
      try {
        return JSON.parse(JSON.stringify(sections || []));
      } catch (_) {
        return sections || [];
      }
    },

    /** Normalize route for de-duplicating static menu vs nav_menus rows */
    normalizeMenuRoute(route) {
      const r = String(route || "").trim().replace(/^\//, "").toLowerCase();
      return r;
    },

    collectRoutesFromItems(items, routeSet) {
      (items || []).forEach((item) => {
        if (item.route) routeSet.add(this.normalizeMenuRoute(item.route));
        if (item.children && item.children.length) this.collectRoutesFromItems(item.children, routeSet);
      });
    },

    /**
     * Sidebar = full static app menu (allMenuSections) filtered by rbac_permissions / getRight,
     * plus any extra rows from nav_menus that are not already in the static tree.
     * Previously we used ONLY nav_menus when non-empty — that hid most items because DB rarely lists every route.
     */
    getMenuSections() {
      const cloned = this.deepCloneMenuSections(this.allMenuSections);
      let ordered = this.applySectionOrder(cloned.map((s) => ({ ...s, title: this.normalizeSectionTitle(s.title) })));
      const byTitle = {};
      ordered.forEach((s) => {
        if (!byTitle[s.title]) byTitle[s.title] = { title: s.title, items: [] };
        byTitle[s.title].items.push(...(s.items || []));
      });

      const staticRoutes = new Set();
      Object.values(byTitle).forEach((sec) => {
        this.collectRoutesFromItems(sec.items, staticRoutes);
      });

      const dbMenus = this.getDbMenus();

      dbMenus.forEach((m, idx) => {
        const isActive = !(m.is_active === false || m.is_active === 0 || String(m.is_active).trim() === '0');
        if (!isActive) return;
        const nr = this.normalizeMenuRoute(m.route);
        if (nr === "reports-hub") return;
        if (nr && staticRoutes.has(nr)) return;
        const section = this.resolveMenuSection(m);
        if (!byTitle[section]) byTitle[section] = { title: section, items: [] };
        byTitle[section].items.push({
          id: "navdb-" + (m.id != null ? m.id : idx),
          title: m.title,
          route: m.route,
          icon: m.icon || "bx bx-circle",
          permission: m.permission_code,
          sort_order: m.sort_order != null ? m.sort_order : 0,
          children: null
        });
      });

      const merged = Object.values(byTitle).map((s) => ({
        ...s,
        items: [...(s.items || [])].sort((a, b) => {
          if (s.title === 'Public Service Board') {
            const recruitmentOrder = {
              'Staff Establishment': 0,
              'Vacancy Management': 1,
              Applicants: 2,
              'Shortlisting Management': 3,
              'Interview Management': 4,
              'Appointment Management': 5,
              'Confirmation & Probation': 6,
              'Acting Appointments': 7,
              'County Attorney': 8
            };
            const ra = recruitmentOrder[String(a.title || '').trim()];
            const rb = recruitmentOrder[String(b.title || '').trim()];
            if (ra != null && rb != null) return ra - rb;
            if (ra != null) return -1;
            if (rb != null) return 1;
          }
          if (s.title === 'Employee Self-service') {
            const essOrder = {
              'My Dashboard': 1,
              'My Profile': 2,
              'My Announcements': 3,
              'My Attendance': 4,
              'My Events': 5,
              'My Leave': 6,
              'My Meetings': 7,
              'My Performance': 8,
              'My Redeployment': 9,
              'My Skills Audit': 10,
              'My Training': 11,
              'My Warnings': 12,
              'My Wealth Declaration': 13,
              'Skills Audit Assessment': 14,
              'My Documents': 15,
              'My Payroll': 16
            };
            const ea = essOrder[String(a.title || '').trim()];
            const eb = essOrder[String(b.title || '').trim()];
            if (ea != null && eb != null) return ea - eb;
            if (ea != null) return -1;
            if (eb != null) return 1;
          }
          if (s.title === 'Skills Audit') {
            const skOrder = {
              'Employees Skills Audits': 1,
              'Monitoring and Evaluation': 2,
              'Skill Gap': 3,
              'Skills Inventory': 4,
              'Skills Audit Report': 5
            };
            const sa = skOrder[String(a.title || '').trim()];
            const sb = skOrder[String(b.title || '').trim()];
            if (sa != null && sb != null) return sa - sb;
            if (sa != null) return -1;
            if (sb != null) return 1;
          }
          return (a.sort_order || 0) - (b.sort_order || 0) ||
            String(a.title || "").localeCompare(String(b.title || ""));
        })
      }));
      return this.applySectionOrder(merged);
    },

    filterMenuItems() {
      const sections = this.getMenuSections();
      const query = this.searchQuery.trim().toLowerCase();
      const newExpanded = { ...this.expandedItems };

      this.filteredMenuSections = sections.map((section) => {
        let items = (section.items || []).map((item) => this.cloneItemVisible(item)).filter(Boolean);
        if (query) {
          items = items.map((item) => this.narrowItemBySearch(item, query)).filter(Boolean);
          items.forEach((item) => {
            if (item.children?.length && item.children.some((ch) => (ch.title || '').toLowerCase().includes(query))) {
              newExpanded[item.id] = true;
            }
          });
        }
        return { ...section, items };
      }).filter((section) => section.items.length > 0)
        .filter((section) => {
          const t = String(section.title || "").trim();
          const govOnly = this.sessionShowGovernorDashboardNavOnly();
          if (t === "Governor" && !govOnly) return false;
          if (t === "Dashboard" && govOnly) return false;
          return true;
        });

      if (query) {
        this.filteredMenuSections.forEach((section) => {
          if (this.isCollapsibleModuleSection(section.title) && section.items.length > 0) {
            const k = this.sectionModuleKey(section.title);
            this.expandedModules = { ...this.expandedModules, [k]: true };
          }
        });
      }

      if (query) this.expandedItems = newExpanded;
      this.$nextTick(() => { this.scrollToActiveMenuItem(); });
    },

    logout(route) {
      const data = new FormData();
      data.append("function", "logout");
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));
      execute(data, authUrl)
        .then(() => { sessionStorage.clear(); this.$router.push(route); })
        .catch((e) => { console.error("Logout error:", e); sessionStorage.clear(); this.$router.push(route); });
      this.showProfileMenu = false;
    },

    toggleTheme() {
      const currentTheme = document.body.getAttribute('data-theme') || 'light';
      const newTheme = currentTheme === 'light' ? 'dark' : 'light';
      document.body.setAttribute('data-theme', newTheme);
      localStorage.setItem('theme', newTheme);
    },

    showHelp() { this.$emit('show-help'); },

    /** Access profile / user category — not RBAC role names */
    formatUserTypeLabel(raw) {
      const s = String(raw || "").trim();
      if (!s) return "";
      return s.replace(/_/g, " ");
    },
    getUserTypeLabel() {
      let raw = "";
      try {
        const ctxRaw = sessionStorage.getItem("rbac_context");
        if (ctxRaw) {
          const ctx = JSON.parse(ctxRaw);
          const uc = ctx?.user_category != null ? String(ctx.user_category).trim() : "";
          if (uc) raw = uc;
        }
      } catch (_) { /* ignore */ }
      if (!raw) {
        const userInfo = JSON.parse(sessionStorage.getItem("userInfo") || "{}");
        raw =
          userInfo.category ||
          userInfo.Category ||
          "";
      }
      const formatted = this.formatUserTypeLabel(raw);
      this.userTypeLabel = formatted || "User";
    },

    initializeMenu() {
      this.filterMenuItems();
      const userInfo = JSON.parse(sessionStorage.getItem("userInfo") || '{}');
      this.sessionNames = userInfo.names || sessionStorage.getItem("sessionNames") || 'User';
      this.getUserTypeLabel();
      const newExpandedItems = { ...this.expandedItems };
      const sections = this.getMenuSections();
      sections.forEach(section => {
        (section.items || []).forEach(item => {
          if (item.children) {
            const hasActiveChild = item.children.some(child =>
              this.filterLeafForNav(child) && this.isActive(child.route)
            );
            if (hasActiveChild) newExpandedItems[item.id] = true;
          }
        });
      });
      this.expandedItems = newExpandedItems;
      this.expandActiveModuleSections();
    },

    async loadRbacAndMenu() {
      if (!sessionStorage.getItem("tokenSession") || !sessionStorage.getItem("isLoggedIn")) return;
      // Always refresh RBAC on nav mount so permission/menu toggles are reflected immediately.
      try {
        await fetchPermissionsAndMenus(authUrl, execute);
      } catch (_) { /* keep existing sessionStorage if request fails */ }
      this.initializeMenu();
    },

    handleClickOutside(event) {
      const profileClicked = event.target.closest('.nav-profile') || event.target.closest('.profile-dropdown');
      if (!profileClicked) this.showProfileMenu = false;
      const mobileButtonClicked = event.target.closest('.mobile-menu-btn');
      if (mobileButtonClicked) return;
      const menuClicked = event.target.closest('.modern-nav');
      if (!menuClicked && this.isMobile) this.isMobileOpen = false;
    },

    scrollToActiveMenuItem() {
      this.$nextTick(() => {
        const activeElement = document.querySelector('.menu-item.active, .submenu-item.active, .menu-item-header.active');
        if (activeElement) {
          const navMenu = this.$refs.navMenu;
          if (navMenu) {
            const elementRect = activeElement.getBoundingClientRect();
            const navMenuRect = navMenu.getBoundingClientRect();
            const relativeTop = elementRect.top - navMenuRect.top;
            navMenu.scrollTo({ top: navMenu.scrollTop + relativeTop - 20, behavior: 'smooth' });
          }
        }
      });
    },

  },

  mounted() {
    this.isMobile = window.innerWidth <= 768;
    this.loadRbacAndMenu();
    const savedTheme = localStorage.getItem('theme') || 'dark';
    document.body.setAttribute('data-theme', savedTheme);
    localStorage.setItem('theme', savedTheme);
    document.addEventListener('click', this.handleClickOutside);
    this.handleResize = () => {
      const isNowMobile = window.innerWidth <= 768;
      const wasMobile = this.isMobile;
      this.isMobile = isNowMobile;
      if (isNowMobile) {
        this.isCollapsed = true;
        if (!wasMobile) this.isMobileOpen = false;
      } else {
        this.isMobileOpen = false;
      }
      this.$emit('nav-toggle', this.isCollapsed || this.isMobileOpen);
    };
    window.addEventListener('resize', this.handleResize);
    this.handleResize();
    this.$nextTick(() => {
      this.$emit('nav-toggle', this.isCollapsed || this.isMobileOpen);
      this.scrollToActiveMenuItem();
    });

    this.handleOpenMobileMenu = () => {
      this.isMobileOpen = true;
      if (this.isCollapsed === false && this.isMobile) {
        this.isCollapsed = true;
      }
      this.$emit('nav-toggle', this.isCollapsed || this.isMobileOpen);
    };
    window.addEventListener('hb-open-mobile-menu', this.handleOpenMobileMenu);
    document.addEventListener('hb-open-mobile-menu', this.handleOpenMobileMenu);
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
    window.removeEventListener('resize', this.handleResize);
    if (this.handleOpenMobileMenu) {
      window.removeEventListener('hb-open-mobile-menu', this.handleOpenMobileMenu);
      document.removeEventListener('hb-open-mobile-menu', this.handleOpenMobileMenu);
    }
  },

  watch: {
    searchQuery() { this.filterMenuItems(); },
    currentRoute: {
      handler() { this.initializeMenu(); this.scrollToActiveMenuItem(); },
      immediate: true
    }
  }
};
</script>

<style scoped>
/*
  Blue Palette derived from #2195d3:
  --blue-900: #0d4f73   very dark
  --blue-800: #0f6899   dark
  --blue-700: #1478b0   medium-dark
  --blue-500: #2195d3   BASE
  --blue-400: #3aa6de   medium-light
  --blue-300: #6dc0e9   light
  --blue-200: #b8dff4   very light
  --blue-100: #e8f4fc   near-white tint
  --blue-50:  #f0f9ff   ultra-light
*/

.nav-root { display: contents; }

/* ===== Nav Shell ===== */
.modern-nav {
  position: fixed;
  top: 0; left: 0;
  height: 100vh;
  width: 280px;
  background: linear-gradient(180deg, #ffffff 0%, #f0f9ff 100%);
  border-right: 2px solid #b8dff4;
  display: flex;
  flex-direction: column;
  z-index: 1000;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 2px 0 20px rgba(33, 149, 211, 0.12);
  overflow: hidden;
}

.modern-nav.collapsed { width: 70px; }

/* County logo (matches login primary URL + fallback) */
.nav-top-logo-wrap {
  flex-shrink: 0;
  padding: 14px 16px 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(180deg, #ffffff 0%, #f0f9ff 100%);
  border-bottom: 1px solid #b8dff4;
}
.nav-top-logo-wrap--collapsed {
  padding: 10px 8px 8px;
}
.nav-top-logo {
  max-width: 100%;
  max-height: 52px;
  width: auto;
  height: auto;
  object-fit: contain;
}
.nav-top-logo-wrap--collapsed .nav-top-logo {
  max-height: 36px;
}
/* ===== Brand ===== */
.nav-brand {
  padding: 14px 12px;
  border-bottom: 1px solid #b8dff4;
  background: linear-gradient(135deg, #2195d3 0%, #0f6899 100%);
  flex-shrink: 0;
}

.brand-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  position: relative;
  min-height: 36px;
}

.brand-content--collapsed {
  justify-content: center;
}

.brand-logo {
  width: 36px; height: 36px;
  border-radius: 8px;
  background: rgba(255,255,255,0.2);
  border: 1px solid rgba(255,255,255,0.3);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}

.logo-img { width: 24px; height: 24px; object-fit: contain; }

.brand-text {
  flex: 1;
  overflow: hidden;
  min-width: 0;
  padding-right: 4px;
}

.brand-title {
  font-size: 13px; font-weight: 700;
  letter-spacing: 0.02em;
  color: #ffffff; margin: 0; line-height: 1.25;
}

.brand-subtitle { font-size: 10px; color: #b8dff4; margin: 2px 0 0 0; }

/* Sidebar collapse — institutional control */
.nav-collapse-btn {
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(255, 255, 255, 0.14);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}

.nav-collapse-btn:hover {
  background: rgba(255, 255, 255, 0.26);
  border-color: rgba(255, 255, 255, 0.55);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.nav-collapse-btn:focus {
  outline: none;
}
.nav-collapse-btn:focus-visible {
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.45);
}

.nav-collapse-btn:active {
  transform: scale(0.97);
}

.nav-collapse-btn__icon {
  font-size: 22px;
  color: #ffffff;
  line-height: 1;
}

/* ===== Profile ===== */
.nav-profile {
  padding: 12px 12px 14px;
  border-bottom: 1px solid #b8dff4;
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;
  background: linear-gradient(180deg, #f0f9ff 0%, #e8f4fc 100%);
  flex-shrink: 0;
}

.profile-avatar { flex-shrink: 0; }

.avatar-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(145deg, #2195d3 0%, #0d4f73 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow:
    0 2px 8px rgba(33, 149, 211, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.85);
}

.avatar-circle i { font-size: 20px; color: white; }

.profile-info {
  flex: 1;
  overflow: hidden;
  min-width: 0;
}

.profile-name {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: #0d4f73;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.profile-meta {
  font-size: 11px;
  font-weight: 500;
  color: #1478b0;
  margin: 3px 0 0 0;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: 0.92;
}

.profile-role {
  font-size: 11px; color: #1478b0;
  margin: 2px 0 0 0;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

.profile-actions { flex-shrink: 0; }

.profile-menu-trigger {
  width: 36px;
  height: 36px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
  border: 1px solid #c5e6f7;
  background: #ffffff;
  cursor: pointer;
  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.15s ease;
  box-shadow: 0 1px 2px rgba(13, 79, 115, 0.06);
}

.profile-menu-trigger:hover {
  background: #ffffff;
  border-color: #2195d3;
  box-shadow: 0 2px 8px rgba(33, 149, 211, 0.18);
}

.profile-menu-trigger:focus {
  outline: none;
}
.profile-menu-trigger:focus-visible {
  box-shadow: 0 0 0 3px rgba(33, 149, 211, 0.35);
}

.profile-menu-trigger:active {
  transform: scale(0.96);
}

.profile-menu-trigger__icon {
  font-size: 18px;
  color: #0d4f73;
  line-height: 1;
}

/* ===== Profile Dropdown ===== */
.profile-dropdown {
  position: absolute;
  top: calc(100% - 4px);
  left: 10px;
  right: 10px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow:
    0 4px 6px -1px rgba(13, 79, 115, 0.07),
    0 12px 28px -4px rgba(13, 79, 115, 0.18);
  border: 1px solid rgba(184, 223, 244, 0.95);
  z-index: 1001;
  overflow: hidden;
  margin-top: 8px;
}

.profile-dropdown-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 14px 12px;
  background: linear-gradient(180deg, #f8fcff 0%, #f0f9ff 100%);
  border-bottom: 1px solid #e8f4fc;
}

.profile-dropdown-header__avatar {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(145deg, #2195d3 0%, #0f6899 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 2px 10px rgba(33, 149, 211, 0.28);
}

.profile-dropdown-header__avatar i {
  font-size: 22px;
  color: #ffffff;
}

.profile-dropdown-header__text {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.profile-dropdown-header__name {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: #0d4f73;
  line-height: 1.25;
  word-break: break-word;
}

.profile-dropdown-header__role {
  font-size: 11px;
  font-weight: 500;
  color: #1478b0;
  line-height: 1.3;
  word-break: break-word;
}

.dropdown-content {
  padding: 6px;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  margin: 2px 0;
  color: #0d4f73;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
  font-size: 13px;
  font-weight: 500;
  border-radius: 8px;
  border: none;
  background: transparent;
  width: 100%;
  box-sizing: border-box;
  text-align: left;
}

.dropdown-item:hover {
  background: #e8f4fc;
  color: #0d4f73;
}

.dropdown-item:focus {
  outline: none;
}
.dropdown-item:focus-visible {
  background: #e8f4fc;
  box-shadow: inset 0 0 0 2px rgba(33, 149, 211, 0.45);
  border-radius: 8px;
}

.dropdown-item__icon-wrap {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: #f0f9ff;
  border: 1px solid #d4eef9;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.dropdown-item__icon-wrap i {
  font-size: 17px;
  color: #2195d3;
}

.dropdown-item__label {
  flex: 1;
}

.dropdown-item--danger .dropdown-item__icon-wrap {
  background: #fef2f2;
  border-color: #fecaca;
}

.dropdown-item--danger .dropdown-item__icon-wrap i {
  color: #dc2626;
}

.dropdown-item--danger {
  color: #b91c1c;
}

.dropdown-item--danger:hover {
  background: #fef2f2;
  color: #991b1b;
}

.dropdown-divider {
  height: 1px;
  background: linear-gradient(90deg, transparent, #b8dff4, transparent);
  margin: 8px 8px;
}

/* ===== Transitions ===== */
.fade-enter-active, .fade-leave-active { transition: all 0.2s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; transform: translateY(-10px); }

/* ===== Search ===== */
.nav-search {
  padding: 10px 12px;
  border-bottom: 1px solid #b8dff4;
  background: #f0f9ff;
  flex-shrink: 0;
}

.search-wrapper { position: relative; }

.search-icon {
  position: absolute; left: 10px; top: 50%;
  transform: translateY(-50%);
  color: #6dc0e9; font-size: 16px;
}

.search-input {
  width: 100%; padding: 8px 12px 8px 34px;
  border: 1px solid #b8dff4; border-radius: 8px;
  background: #ffffff; color: #0d4f73; font-size: 12px;
  transition: all 0.2s;
}

.search-input:focus {
  outline: none; border-color: #2195d3;
  box-shadow: 0 0 0 3px rgba(33, 149, 211, 0.15);
}

.search-input::placeholder { color: #6dc0e9; }

/* ===== Nav Menu ===== */
.nav-menu {
  flex: 1; overflow-y: auto;
  padding: 8px 0; scroll-behavior: smooth;
  background: #ffffff;
}

.menu-section { margin-bottom: 4px; }
.section-header { padding: 8px 12px 3px 12px; }

.section-header--dropdown {
  width: calc(100% - 12px);
  margin: 0 6px 2px;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 8px;
  text-align: left;
  box-sizing: border-box;
}
.section-header--dropdown:hover {
  background: #e8f4fc;
}
.section-header--dropdown .section-title {
  flex: 1;
  text-align: left;
}
.section-header-chevron {
  font-size: 14px;
  color: #6dc0e9;
  flex-shrink: 0;
}
.section-header--dropdown-active .section-title {
  color: #2195d3;
}

.section-title {
  font-size: 10px; font-weight: 700;
  color: #6dc0e9; text-transform: uppercase;
  letter-spacing: 0.8px; margin: 0;
}

.menu-items { display: flex; flex-direction: column; gap: 1px; }
.menu-item-wrapper { position: relative; }

/* ===== Menu Items ===== */
.menu-item,
.menu-item-header {
  display: flex; align-items: center;
  padding: 8px 12px; cursor: pointer;
  transition: all 0.2s; position: relative;
  border-radius: 8px; margin: 0 6px;
}

.menu-item:hover,
.menu-item-header:hover { background: #e8f4fc; }

.menu-item.active,
.menu-item-header.active {
  background: linear-gradient(135deg, #2195d3 0%, #1478b0 100%);
  box-shadow: 0 2px 10px rgba(33, 149, 211, 0.35);
}

.menu-item.collapsed,
.menu-item-header.collapsed { justify-content: center; padding: 8px; position: relative; }
/* Extended hit area when collapsed so tooltip doesn't disappear before click */
.menu-item.collapsed::after,
.menu-item-header.collapsed::after {
  content: '';
  position: absolute;
  left: 100%;
  top: 0;
  width: 220px;
  height: 100%;
  z-index: 999;
}

.menu-icon {
  flex-shrink: 0; width: 20px; height: 20px;
  display: flex; align-items: center; justify-content: center;
}

.menu-icon i { font-size: 16px; color: #3aa6de; transition: all 0.2s; }
.menu-item.active .menu-icon i,
.menu-item-header.active .menu-icon i { color: #ffffff; }
.menu-item:hover .menu-icon i,
.menu-item-header:hover .menu-icon i { color: #0f6899; }

.menu-text {
  flex: 1; margin-left: 10px;
  display: flex; align-items: center; justify-content: space-between;
  overflow: hidden;
}

.menu-title {
  font-size: 13px; font-weight: 500;
  color: #0d4f73;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

.menu-item.active .menu-title,
.menu-item-header.active .menu-title { color: #ffffff; }

.menu-badge {
  font-size: 10px; font-weight: 600;
  padding: 2px 5px; border-radius: 8px;
  background: #ef4444; color: #ffffff; margin-left: 6px;
}

.menu-arrow, .menu-arrow-collapsed { flex-shrink: 0; margin-left: 6px; }
.menu-arrow i, .menu-arrow-collapsed i { font-size: 14px; color: #6dc0e9; transition: all 0.2s; }
.menu-arrow-collapsed { margin-left: auto; }

.menu-item-header.active .menu-arrow i,
.menu-item-header.active .menu-arrow-collapsed i { color: #ffffff; }

/* ===== Tooltip ===== */
.menu-tooltip {
  position: absolute; left: calc(100% + 10px); top: 50%;
  transform: translateY(-50%);
  background: #0d4f73; color: #ffffff;
  padding: 5px 10px; border-radius: 6px;
  font-size: 11px; font-weight: 500;
  white-space: nowrap; opacity: 0;
  pointer-events: none; transition: opacity 0.2s; z-index: 1000;
}

.menu-tooltip::before {
  content: ''; position: absolute; left: -4px; top: 50%;
  transform: translateY(-50%);
  border-top: 4px solid transparent; border-bottom: 4px solid transparent;
  border-right: 4px solid #0d4f73;
}

.menu-item.collapsed:hover .menu-tooltip,
.menu-item-header.collapsed:hover .menu-tooltip { opacity: 1; }

/* ===== Submenu ===== */
.submenu {
  width: calc(100% - 12px);
  margin: 2px 6px 4px 6px;
  background: #f0f9ff;
  border-radius: 8px;
  border: 1px solid #b8dff4;
  border-left: 3px solid #2195d3;
  overflow: hidden;
  padding: 4px 0 4px 26px;
}

.submenu-item {
  display: flex; align-items: center;
  padding: 6px 12px; cursor: pointer;
  transition: all 0.2s; margin: 1px 0; border-radius: 6px;
}

.submenu-item:hover { background: #b8dff4; }

.submenu-item.active {
  background: linear-gradient(90deg, #e8f4fc 0%, #b8dff4 100%);
  border-left: 2px solid #2195d3;
}

.submenu-icon {
  flex-shrink: 0; width: 18px; height: 18px;
  display: flex; align-items: center; justify-content: center;
}

.submenu-icon i { font-size: 14px; color: #3aa6de; }
.submenu-item.active .submenu-icon i { color: #0f6899; }

.submenu-text {
  flex: 1; margin-left: 8px;
  display: flex; align-items: center; justify-content: space-between;
  overflow: hidden;
}

.submenu-title { font-size: 12px; color: #1478b0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.submenu-item.active .submenu-title { color: #0d4f73; font-weight: 600; }

.submenu-badge {
  font-size: 9px; font-weight: 600; padding: 1px 4px;
  border-radius: 6px; background: #2195d3; color: #ffffff; margin-left: 4px;
}

/* ===== Slide animation ===== */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  max-height: 500px; overflow: hidden;
}

.slide-down-enter-from, .slide-down-leave-to { max-height: 0; opacity: 0; }

/* ===== Footer ===== */
.nav-footer {
  border-top: 1px solid #b8dff4;
  padding: 10px 12px;
  background: #e8f4fc;
  flex-shrink: 0;
}

.footer-content { display: flex; justify-content: space-around; }

.footer-item {
  display: flex; flex-direction: column; align-items: center;
  gap: 3px; cursor: pointer; transition: all 0.2s;
  padding: 6px 14px; border-radius: 6px;
}

.footer-item:hover { background: #b8dff4; }
.footer-item i { font-size: 16px; color: #2195d3; }
.footer-item:hover i { color: #0d4f73; }
.footer-item span { font-size: 10px; color: #1478b0; }

/* ===== Scrollbar ===== */
.nav-menu::-webkit-scrollbar { width: 4px; }
.nav-menu::-webkit-scrollbar-track { background: #e8f4fc; }
.nav-menu::-webkit-scrollbar-thumb { background: #6dc0e9; border-radius: 2px; }
.nav-menu::-webkit-scrollbar-thumb:hover { background: #2195d3; }

/* ===== Mobile Backdrop ===== */
.mobile-menu-backdrop {
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(13, 79, 115, 0.5);
  z-index: 998; backdrop-filter: blur(2px);
}

/* ===== Responsive ===== */
@media (max-width: 768px) {
  .modern-nav {
    transform: translateX(-100%);
    width: 280px; position: fixed; top: 0; left: 0;
    height: 100vh; z-index: 1000;
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .modern-nav.mobile-open { transform: translateX(0); }
  .modern-nav.collapsed { transform: translateX(-100%); }
  .modern-nav.collapsed.mobile-open { transform: translateX(0); }
  .nav-collapse-btn { display: none !important; }

  .mobile-menu-btn {
    position: fixed; top: 16px; left: 16px;
    width: 44px; height: 44px; border-radius: 12px;
    background: linear-gradient(135deg, #2195d3 0%, #0f6899 100%);
    border: none; display: flex; align-items: center; justify-content: center;
    cursor: pointer; z-index: 999;
    box-shadow: 0 4px 12px rgba(33, 149, 211, 0.45);
    transition: all 0.2s; color: #ffffff; font-size: 24px;
  }

  .mobile-menu-btn:active { transform: scale(0.95); }
  .mobile-menu-btn:hover { box-shadow: 0 6px 16px rgba(33, 149, 211, 0.6); }
}

/* ===== Dark Theme ===== */
[data-theme="dark"] .modern-nav {
  background: linear-gradient(180deg, #0d4f73 0%, #0a3d5a 100%);
  border-right-color: #1478b0;
  box-shadow: 2px 0 20px rgba(0, 0, 0, 0.3);
}

[data-theme="dark"] .nav-brand {
  background: linear-gradient(135deg, #082d42 0%, #0a3d5a 100%);
  border-bottom-color: #1478b0;
}

[data-theme="dark"] .brand-title { color: #ffffff; }
[data-theme="dark"] .brand-subtitle { color: #6dc0e9; }

[data-theme="dark"] .nav-collapse-btn {
  border-color: rgba(255, 255, 255, 0.28);
  background: rgba(255, 255, 255, 0.08);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
}
[data-theme="dark"] .nav-collapse-btn:hover {
  background: rgba(255, 255, 255, 0.16);
  border-color: rgba(255, 255, 255, 0.42);
}
[data-theme="dark"] .nav-collapse-btn:focus-visible {
  box-shadow: 0 0 0 3px rgba(109, 192, 233, 0.45);
}

[data-theme="dark"] .nav-profile {
  background: linear-gradient(180deg, #072f47 0%, #082d42 100%);
  border-bottom-color: #1478b0;
}

[data-theme="dark"] .profile-name { color: #e8f4fc; }
[data-theme="dark"] .profile-meta,
[data-theme="dark"] .profile-role { color: #6dc0e9; }

[data-theme="dark"] .profile-menu-trigger {
  background: rgba(13, 79, 115, 0.65);
  border-color: #1478b0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}
[data-theme="dark"] .profile-menu-trigger:hover {
  background: #0f6899;
  border-color: #2195d3;
}
[data-theme="dark"] .profile-menu-trigger:focus-visible {
  box-shadow: 0 0 0 3px rgba(33, 149, 211, 0.45);
}
[data-theme="dark"] .profile-menu-trigger__icon {
  color: #e8f4fc;
}

[data-theme="dark"] .profile-dropdown {
  background: #0a3d5a;
  border-color: #1478b0;
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.35),
    0 2px 8px rgba(0, 0, 0, 0.2);
}

[data-theme="dark"] .profile-dropdown-header {
  background: linear-gradient(180deg, #0d4f73 0%, #0a3d5a 100%);
  border-bottom-color: #1478b0;
}

[data-theme="dark"] .profile-dropdown-header__name {
  color: #e8f4fc;
}

[data-theme="dark"] .profile-dropdown-header__role {
  color: #6dc0e9;
}

[data-theme="dark"] .dropdown-item {
  color: #e8f4fc;
}

[data-theme="dark"] .dropdown-item:hover {
  background: rgba(33, 149, 211, 0.22);
  color: #ffffff;
}

[data-theme="dark"] .dropdown-item__icon-wrap {
  background: rgba(13, 79, 115, 0.85);
  border-color: #1478b0;
}

[data-theme="dark"] .dropdown-item__icon-wrap i {
  color: #6dc0e9;
}

[data-theme="dark"] .dropdown-item--danger .dropdown-item__icon-wrap {
  background: rgba(220, 38, 38, 0.15);
  border-color: rgba(248, 113, 113, 0.35);
}

[data-theme="dark"] .dropdown-item--danger:hover {
  background: rgba(220, 38, 38, 0.2);
  color: #fecaca;
}

[data-theme="dark"] .dropdown-divider {
  background: linear-gradient(90deg, transparent, rgba(20, 120, 176, 0.6), transparent);
}

[data-theme="dark"] .nav-search { background: #082d42; border-bottom-color: #1478b0; }

[data-theme="dark"] .search-input {
  background: #0d4f73; border-color: #1478b0; color: #e8f4fc;
}

[data-theme="dark"] .search-input:focus {
  background: #0f6899; border-color: #2195d3;
  box-shadow: 0 0 0 3px rgba(33, 149, 211, 0.25);
}

[data-theme="dark"] .search-input::placeholder { color: #3aa6de; }

[data-theme="dark"] .nav-menu { background: #0d4f73; }
[data-theme="dark"] .section-title { color: #3aa6de; }
[data-theme="dark"] .section-header--dropdown:hover { background: #0f6899; }
[data-theme="dark"] .section-header-chevron { color: #3aa6de; }
[data-theme="dark"] .section-header--dropdown-active .section-title { color: #6dc0e9; }
[data-theme="dark"] .menu-title { color: #b8dff4; }

[data-theme="dark"] .menu-item:hover,
[data-theme="dark"] .menu-item-header:hover { background: #0f6899; }

[data-theme="dark"] .menu-item.active,
[data-theme="dark"] .menu-item-header.active {
  background: linear-gradient(135deg, #2195d3 0%, #3aa6de 100%);
  box-shadow: 0 2px 10px rgba(33, 149, 211, 0.4);
}

[data-theme="dark"] .menu-icon i { color: #6dc0e9; }
[data-theme="dark"] .menu-item:hover .menu-icon i,
[data-theme="dark"] .menu-item-header:hover .menu-icon i { color: #b8dff4; }
[data-theme="dark"] .menu-item.active .menu-icon i,
[data-theme="dark"] .menu-item-header.active .menu-icon i { color: #ffffff; }

[data-theme="dark"] .menu-arrow i,
[data-theme="dark"] .menu-arrow-collapsed i { color: #3aa6de; }
[data-theme="dark"] .menu-item-header.active .menu-arrow i,
[data-theme="dark"] .menu-item-header.active .menu-arrow-collapsed i { color: #ffffff; }

[data-theme="dark"] .submenu {
  background: #082d42;
  border-color: #1478b0;
  border-left-color: #2195d3;
}

[data-theme="dark"] .submenu-item:hover { background: #0f6899; }
[data-theme="dark"] .submenu-item.active {
  background: rgba(33, 149, 211, 0.2);
  border-left-color: #3aa6de;
}

[data-theme="dark"] .submenu-title { color: #6dc0e9; }
[data-theme="dark"] .submenu-item.active .submenu-title { color: #b8dff4; }
[data-theme="dark"] .submenu-icon i { color: #3aa6de; }
[data-theme="dark"] .submenu-item.active .submenu-icon i { color: #6dc0e9; }

[data-theme="dark"] .nav-footer { background: #082d42; border-top-color: #1478b0; }
[data-theme="dark"] .footer-item:hover { background: #0f6899; }
[data-theme="dark"] .footer-item i { color: #6dc0e9; }
[data-theme="dark"] .footer-item:hover i { color: #b8dff4; }
[data-theme="dark"] .footer-item span { color: #3aa6de; }

[data-theme="dark"] .nav-menu::-webkit-scrollbar-track { background: #082d42; }
[data-theme="dark"] .nav-menu::-webkit-scrollbar-thumb { background: #1478b0; }
[data-theme="dark"] .nav-menu::-webkit-scrollbar-thumb:hover { background: #2195d3; }

[data-theme="dark"] .profile-dropdown {
  background: #0d4f73; border-color: #1478b0;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
}

[data-theme="dark"] .dropdown-item { color: #b8dff4; }
[data-theme="dark"] .dropdown-item i { color: #6dc0e9; }
[data-theme="dark"] .dropdown-item:hover { background: #0f6899; color: #ffffff; }
[data-theme="dark"] .dropdown-divider { background: #1478b0; }

[data-theme="dark"] .mobile-menu-btn {
  background: linear-gradient(135deg, #2195d3 0%, #0f6899 100%);
  box-shadow: 0 4px 12px rgba(33, 149, 211, 0.5);
}

[data-theme="dark"] .mobile-menu-btn:hover { box-shadow: 0 6px 16px rgba(33, 149, 211, 0.65); }
</style>